/**
 * @mybusinessmate/ui-tokens  — v2 (light, Jomo-style)
 *
 * Shared design tokens for the MyBusinessMate mobile (React Native) and web apps.
 * Pure TypeScript, zero runtime dependencies.
 *
 * Source of truth: Figma "mybusinessmate mobile" → 01 · Foundations (variables:
 * Color / Color · Dark (reference) / Dimension) and the text + effect styles.
 *
 * Conventions
 *  - All sizes are unitless numbers in px (React Native native unit).
 *    Web: use `px()` / `rem()` helpers at the bottom.
 *  - Light mode is the product default. `darkColors` is a reference palette only
 *    (not shipped in phase 1).
 *  - Prefer semantic tokens (`colors.text.primary`) over raw palette values.
 */

/* ------------------------------------------------------------------ */
/* 1. Palette — raw values. Don't use directly in components.         */
/* ------------------------------------------------------------------ */

export const palette = {
  white: '#ffffff',
  black: '#000000',
  ink: '#0b0b12',
  inkToast: '#16161d', // Figma P01 toast surface: a touch lighter than ink so it lifts off black buttons

  // Brand (logo gradient: blue → violet → pink → coral)
  blue: '#2952f8',
  blueDeep: '#0119ef',
  violet: '#b83efb',
  pink: '#fd4a9c',
  pink50: '#fdeef7', // Figma C03.2 progress card: the pink wash that fades to white
  slate400: '#94a3b8', // Figma C03.2: the bar beside a queued task
  coral: '#fd393a',
  reelOrange: '#fda048', // Reel/video media gradient (orange → pink → violet), paired with pink/violet above

  // UI blues (buttons, hero CTA, charts)
  blue50: '#eef3ff',
  blue100: '#eaf0ff',
  blue150: '#dde6ff', // Chat outgoing bubble bg (Figma "Message Bubble" component)
  blue175: '#c7d7fe', // Figma C03.4: the outline of an opened login
  blue200: '#bfd3fe',
  blue250: '#9db2f5', // Chat voice-note waveform, unplayed portion
  blue300: '#7c93f8', // Home dashboard traffic-sources chart — "Direct" segment
  blue600: '#2563eb',
  blue700: '#1d4ed8',
  blue75: '#e8efff', // Figma A05.7: the Medium priority pill
  // Hero CTA gradient stops (Figma "Button" component, Style=Brand)
  heroBlueStart: '#2966ed',
  heroBlueMid: '#5c99fa',
  heroBlueEnd: '#a8d4ff',
  indigo50: '#eef0ff',
  indigo500: '#6366f1',
  indigo600: '#4f46e5',
  indigo700: '#4338ca',
  // Home dashboard bar-chart fill (Figma "Img - Website views per day" bars)
  chartBarRestStart: '#d6e4fc',
  chartBarRestEnd: '#bcd3fa',

  // Neutrals (cool grey)
  grey25: '#f6f8fa',
  grey50: '#f5f6f9',
  grey75: '#f3f4f7',
  grey100: '#eceff3',
  grey150: '#eceef2',
  grey200: '#e8ebf0',
  grey250: '#e6e8ee',
  grey300: '#e3e6eb',
  grey350: '#e4e6eb',
  grey375: '#d6dae1', // OTP cell outline (Figma web "C01.9 · Verify email")
  grey400: '#cdd1d9',
  grey450: '#c9ced8',
  grey500: '#b8bdc7',
  grey525: '#9aa1ad', // Figma A05.7: the dot of the Low priority pill
  grey550: '#b4b9c2',
  grey600: '#8a91a0',
  grey650: '#858b95',
  grey700: '#5b6272',
  grey750: '#475569',
  grey800: '#334155',
  grey850: '#2a2a33',
  grey900: '#0f172a',

  // Sky (Home background)
  skyTop: '#2a78f0',
  skyMid: '#6ca8f6',
  skyHorizon: '#d9e8fc',
  cloud: '#ffffff',
  cloudBlush: '#f6d9f2',
  // Sky image's landing shade (Figma "Welcome" screen's "Fade" rectangle fades into this)
  skyFade: '#f1f3f7',

  // Feedback
  green500: '#22c55e', // Figma P01 toast: the success tick on the dark toast
  green600: '#16a34a',
  green700: '#15803d',
  green50: '#e7f8ee',
  amber600: '#d97706',
  amber700: '#b45309',
  amber500: '#f59e0b',
  amber400: '#fbbf24',
  amber50: '#fef4e2',
  amber25: '#fffbeb', // Figma A05.7b: the at-capacity gauge
  amber100: '#fef3c7', // Figma A05.7: the High priority pill
  amber300: '#fcd34d', // Figma A05.7b: the at-capacity gauge outline
  amber800: '#92400e', // Figma A05.7b: the at-capacity sentence
  noteCream: '#fdf6e3', // Figma A05.7d: the latest-note block of the quick peek
  red500: '#ef4444', // Figma P01 toast: the error cross on the dark toast
  red600: '#dc2626',
  red700: '#b91c1c',
  red50: '#fdecec',
  fuchsia700: '#a21caf',
  fuchsia50: '#fbeeff',
  orange600: '#ea580c',
  orange700: '#c2410c',
  orange900: '#7c2d12',
  teal700: '#0f766e',

  // Report Plan Card tiers (Figma "plan/*" variable collection) - the pricing/upsell card's own
  // accent system, distinct from the generic brand/tag palettes above.
  planVioletSoft: '#f6eeff',
  planGold: '#f5a623',
  planGoldLight: '#e8b10c',
  planGoldDeep: '#e8890c',
  planGoldSoft: '#3a2e14',
  planInk: '#111318',
  planInkRaised: '#1b1e26',
  planInkBorder: '#2a2e38',
  planInkText: '#a3a8b5',
  planLavender: '#e4ccff',
} as const;

/* ------------------------------------------------------------------ */
/* 2. Semantic colours (light — default)                              */
/* ------------------------------------------------------------------ */

export const colors = {
  brand: {
    primary: palette.blue600, // buttons, links-on-blue, selected states
    primaryPressed: palette.blue700,
    primaryLight: palette.blue100, // tinted chips / selected rows
    logo: palette.blue, // brand mark blue (Figma brand/blue)
    logoDeep: palette.blueDeep,
    indigo: palette.indigo600,
    violet: palette.violet,
    pink: palette.pink,
    coral: palette.coral,
  },

  /**
   * Figma C03.1 / C03.2 (the client's Projects): the fill of a project's progress bar by domain, and the 4px bar beside
   * a task by where it stands. A task that is simply in progress takes its type's colour (a post blue, a reel violet).
   */
  accent: {
    website: palette.blue600,
    marketing: palette.pink,
    automation: palette.violet,
    waiting: palette.pink,
    changes: palette.amber500,
    queued: palette.slate400,
    post: palette.blue600,
    reel: palette.violet,
  },

  surface: {
    canvas: palette.grey25, // screen background (non-Home screens)
    card: palette.white,
    elevated: palette.white, // sheets, popovers
    muted: palette.grey50,
    tint: palette.grey75, // segmented control track, icon tiles, secondary buttons
    field: palette.grey100, // input fill
    inverse: palette.ink,
    skyFade: palette.skyFade, // screen bg behind a sky-image hero (Figma "Welcome" screen)
    revealed: palette.blue50, // Figma C03.4: a login that is open, its values showing
  },

  text: {
    primary: palette.ink, // headings, body on light
    strong: palette.grey900,
    body: palette.grey800,
    secondary: palette.grey700, // subtitles, meta
    section: palette.grey650, // section titles / overlines
    tertiary: palette.grey600,
    muted: palette.grey550, // placeholders, chevrons
    disabled: palette.grey500,
    onDark: palette.white, // text on hero gradient / black buttons
    link: palette.blue,
  },

  border: {
    default: palette.grey250,
    line: palette.grey150, // dividers inside cards
    list: palette.grey200, // grouped list outline
    input: palette.grey300,
    strong: palette.grey400,
    dashed: palette.grey450,
    cell: palette.grey375, // resting OTP code cell outline (web Verify email / 2FA)
    revealed: palette.blue175, // Figma C03.4: the outline of a login that is open
  },

  action: {
    primary: palette.ink, // black pill button
    primaryPressed: palette.grey850,
    secondary: palette.white, // white pill button
    secondaryBorder: palette.grey200,
    disabled: palette.grey350,
    destructive: palette.red600,
  },

  focus: { ring: palette.blue },

  feedback: {
    success: palette.green600,
    successText: palette.green700,
    successBg: palette.green50,
    warning: palette.amber600,
    warningBg: palette.amber50,
    error: palette.red600,
    errorBg: palette.red50,
    info: palette.blue,
    infoBg: palette.blue100,
  },

  /** Figma P01 toasts: one dark surface; only the icon carries the tone (green tick, amber clock/sparkle, red cross). */
  toast: {
    surface: palette.inkToast,
    text: palette.white,
    success: palette.green500,
    warning: palette.amber500,
    error: palette.red500,
  },

  /** Figma A05.7 "My work": the pill beside a card's type (a dot and a word), by how soon it matters. Never red. */
  priority: {
    high: { bg: palette.amber100, dot: palette.amber600, text: palette.amber700 },
    medium: { bg: palette.blue75, dot: palette.blue600, text: palette.blue700 },
    low: { bg: '#f1f3f6', dot: palette.grey525, text: palette.grey700 },
  },

  /** Figma A05.7 / A05.7b: the gauge of active tasks. Filled segments are dark; at capacity it turns amber, never red. */
  capacity: {
    segment: palette.ink,
    segmentEmpty: palette.grey300,
    full: {
      bg: palette.amber25,
      border: palette.amber300,
      segment: palette.amber600,
      label: palette.amber700,
      text: palette.amber800,
    },
  },

  /** Figma A05.7: the grey lane behind a column of cards, and the amber note on a card sent back. */
  board: {
    column: palette.grey100,
    note: '#fff7e6',
    noteText: palette.grey800,
    peekNote: palette.noteCream,
  },

  overlay: {
    scrim: 'rgba(11, 11, 18, 0.40)', // behind bottom sheets
    scrimLight: 'rgba(15, 23, 42, 0.14)', // behind popover menus
    glass: 'rgba(255, 255, 255, 0.75)', // header buttons on sky
  },

  sky: {
    top: palette.skyTop,
    mid: palette.skyMid,
    horizon: palette.skyHorizon,
    cloud: palette.cloud,
    cloudBlush: palette.cloudBlush,
  },

  /**
   * Status Tag component tones (Figma "Status Tag", node 116:805 — 7 tones × 2 styles).
   * Each tone's top-level `bg`/`text` is the "Soft" style; `solid` is the filled, white-on-color
   * style added later in Figma (e.g. the Notifications sheet's "3 new" unread badge is
   * Tone=Blue, Style=Solid). For every tone except blue, `solid.bg` reuses that same tone's own
   * `text` color as a solid fill - Figma does this consistently. Blue is the one exception: its
   * real solid usage (the "3 new" badge) uses `blue600`, not `blue700` the way `text` does.
   */
  tag: {
    blue: { bg: palette.blue50, text: palette.blue700, solid: { bg: palette.blue600, text: palette.white } },
    pink: { bg: palette.fuchsia50, text: palette.fuchsia700, solid: { bg: palette.fuchsia700, text: palette.white } },
    indigo: { bg: palette.indigo50, text: palette.indigo700, solid: { bg: palette.indigo700, text: palette.white } },
    amber: { bg: '#fff7e6', text: palette.amber700, solid: { bg: palette.amber700, text: palette.white } },
    red: { bg: '#feefef', text: palette.red700, solid: { bg: palette.red700, text: palette.white } },
    green: { bg: '#e8f7ee', text: palette.green700, solid: { bg: palette.green700, text: palette.white } },
    grey: { bg: '#f1f3f6', text: palette.grey750, solid: { bg: palette.grey750, text: palette.white } },
  },

  /** Data sources shown on Home (engagement dots, charts). */
  channel: {
    website: palette.blue600,
    socials: palette.pink,
    gbp: palette.amber500,
    whatsapp: '#25d366',
    instagram: '#e1306c',
    facebook: '#1877f2',
  },

  /** Home dashboard "Where visitors come from" stacked bar (Figma "Img - Traffic sources"). */
  acquisition: {
    organic: palette.blue600,
    direct: palette.blue300,
    social: palette.pink,
    referral: palette.amber500,
    ads: palette.green600,
  },

  /** Charts: highlight bar vs. resting bars. */
  chart: {
    primary: palette.blue600,
    rest: palette.blue200,
    line: palette.blue600,
    grid: palette.grey150,
    axis: palette.grey550,
    up: palette.green700,
    down: palette.red600,
  },

  /** Chat: message bubbles and presence. */
  chat: {
    outgoingBg: palette.blue150,
    incomingBg: palette.white,
    sending: palette.grey550,
    delivered: palette.grey600,
    read: palette.blue600,
    failed: palette.red600,
    online: palette.green600,
    typing: palette.green700,
    waveformPlayed: palette.blue,
    waveformUnplayed: palette.blue250,
  },
} as const;

/** Reference only — dark mode is not in phase 1. Mirrors `Color · Dark (reference)` in Figma. */
export const darkColors = {
  brand: { logo: '#5b7bff', logoDeep: '#3d57ff', violet: '#c86bff', pink: '#ff6fb0', coral: '#ff5c5d' },
  sky: { top: '#0d1e4a', mid: '#1e3a7a', horizon: '#2b2f5c', cloud: '#8c93c9', cloudBlush: '#6e4f8f' },
  surface: { canvas: '#0b0b12', card: '#16161f', muted: '#1e1e29', elevated: '#23232f' },
  text: {
    primary: '#f4f5f8',
    secondary: '#a9afbd',
    tertiary: '#7c8292',
    disabled: '#4a4f5c',
    onDark: '#0b0b12',
    link: '#7c96ff',
  },
  border: { default: '#2a2a36', strong: '#3b3b4a', dashed: '#3b3b4a' },
  action: { primary: '#f4f5f8', primaryPressed: '#d6d8de', secondary: '#23232f', disabled: '#2a2a36' },
  focus: { ring: '#7c96ff' },
  feedback: {
    success: '#34d399',
    successBg: '#12301f',
    warning: '#fbbf24',
    warningBg: '#332711',
    error: '#f87171',
    errorBg: '#3a1717',
    info: '#7c96ff',
    infoBg: '#16204a',
  },
  overlay: { scrim: '#000000' },
} as const;

/* ------------------------------------------------------------------ */
/* 3. Gradients                                                       */
/* ------------------------------------------------------------------ */

/**
 * `angle` follows CSS (0 = to top, 90 = to right). For RN LinearGradient use
 * `gradientToRN()` below.
 */
export const gradients = {
  /**
   * Button/Hero + Meeting CTA (blue). Figma "Button" component, Style=Brand:
   * stops are NOT evenly spaced (mid stop sits at 60%, not 50%) - `locations`
   * carries that, see gradientToRN/gradientToCSS below.
   */
  hero: {
    angle: 90,
    stops: [palette.heroBlueStart, palette.heroBlueMid, palette.heroBlueEnd],
    locations: [0, 0.6, 1],
  },
  /** Meeting CTA · Business consultation (Pro) */
  pro: { angle: 90, stops: [palette.ink, '#312e81', palette.ink] },
  /** Meeting CTA · Booked */
  success: { angle: 90, stops: [palette.green600, '#059669', palette.green700] },
  /** Home dashboard "See This Month Report" CTA (Figma "Home CTA" instance) */
  ctaPrimary: { angle: 90, stops: [palette.blue600, palette.indigo600, palette.blue700] },
  /** Home dashboard website-views bar chart — resting (non-peak) day bar */
  chartBarRest: { angle: 180, stops: [palette.chartBarRestStart, palette.chartBarRestEnd] },
  /** Home dashboard website-views bar chart — peak day bar (Figma: Friday) */
  chartBarPeak: { angle: 180, stops: [palette.blue600, palette.indigo500] },
  /** Figma C03.1 "Waiting for you" card: a blue wash from the top that fades to white */
  cardWashBlue: { angle: 180, stops: [palette.blue50, palette.white] },
  /** Figma C03.2 progress card: the same fade in pink */
  cardWashPink: { angle: 180, stops: [palette.pink50, palette.white] },
  /** Logo / avatar ring */
  brand: { angle: 135, stops: [palette.blue, palette.violet, palette.pink] },
  /**
   * Reel/video preview thumbnail background (Figma "Task · Reel review", node 81:2197).
   * Same orange→pink hand as the Task Row reel icon tile (`task-row.tsx`'s local gradient),
   * extended with a violet third stop for the larger preview. Figma's raw gradientAngle for
   * this node is 283° in Figma's own coordinate convention; 135 is the equivalent CSS angle
   * that renders the same top-left (orange) → bottom-right (violet) diagonal.
   */
  reelPreview: { angle: 135, stops: [palette.reelOrange, palette.pink, palette.violet], locations: [0, 0.55, 1] },
  /**
   * Website task "staging preview" hero mockup background (Figma "Task · Website", node
   * 81:2649). Same top-left → bottom-right diagonal convention as `reelPreview` above; Figma's
   * raw gradientAngle for this node is 297°.
   */
  websiteHero: { angle: 135, stops: [palette.orange900, palette.orange700, palette.amber500], locations: [0, 0.6, 1] },
  /**
   * "New request" sheet icon tiles (Figma "5.9 New request sheet", node 137:2586). Same
   * top-left → bottom-right diagonal convention as `reelPreview`/`websiteHero` above; Figma's
   * raw gradientAngle for these three tiles is 305°/286°/305°.
   */
  requestTilePost: { angle: 135, stops: ['#fdba74', '#f97316', '#be123c'], locations: [0, 0.5, 1] },
  requestTileReel: { angle: 135, stops: ['#312e81', '#7c3aed', '#ec4899'], locations: [0, 0.55, 1] },
  requestTileWebsite: { angle: 135, stops: [palette.blue200, '#60a5fa', palette.blue600], locations: [0, 0.6, 1] },
  /** Home sky background (top → bottom) */
  sky: { angle: 180, stops: [palette.skyTop, palette.skyMid, palette.skyHorizon] },
  /**
   * Welcome screen · the sky image fades into the card background underneath it
   * (Figma "Fade" rectangle, "1.2 Welcome" frame). Stops carry alpha directly since
   * they fade `skyFade` from transparent to solid, not between two opaque colors.
   */
  skyFade: {
    angle: 180,
    stops: ['rgba(241, 243, 247, 0)', 'rgba(241, 243, 247, 0.95)', palette.skyFade],
    locations: [0, 0.3, 1],
  },
  /** Widgets / soft cards */
  softBlue: { angle: 160, stops: ['#dce7ff', '#f4f7ff', palette.white] },
  /**
   * Bottom-of-scroll fade into the canvas background, behind the floating tab bar (Figma
   * "Gradient" rectangle reused across Chat/Work/Projects list screens, e.g. "4.1 Chat ·
   * Channels"). Transparent at the top so content fades out before it reaches the solid mask.
   */
  canvasFade: {
    angle: 180,
    stops: ['rgba(246, 248, 250, 0)', palette.grey25, palette.grey25],
    locations: [0, 0.45, 1],
  },
  /** Report Plan Card "Get started" button, tier=plus (Right Hand Mate). */
  planPlus: { angle: 270, stops: [palette.violet, palette.pink] },
  /** Report Plan Card "Get started" button, tier=pro (Business Partner). */
  planPro: { angle: 270, stops: [palette.planGold, palette.planGoldLight] },
} as const;

/* ------------------------------------------------------------------ */
/* 4. Typography — Outfit (display) + Geist (UI)                      */
/* ------------------------------------------------------------------ */

export const fontFamily = {
  /** Web stacks */
  heading: "Outfit, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  sans: "Geist, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  mono: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
} as const;

/**
 * React Native font names — must match the files you load
 * (expo-font / react-native.config.js). Adjust if your file names differ.
 */
export const nativeFonts = {
  outfit: { regular: 'Outfit-Regular', medium: 'Outfit-Medium', semibold: 'Outfit-SemiBold', bold: 'Outfit-Bold' },
  geist: { regular: 'Geist-Regular', medium: 'Geist-Medium', semibold: 'Geist-SemiBold', bold: 'Geist-Bold' },
  mono: { regular: 'GeistMono-Regular' },
} as const;

export const fontWeight = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

type TextStyleToken = {
  family: 'heading' | 'sans' | 'mono';
  weight: keyof typeof fontWeight;
  size: number;
  lineHeight: number;
  letterSpacing: number;
  textTransform?: 'uppercase';
};

/** Mirrors Figma text styles 1:1 (names flattened: "Heading/H1" → headingH1). */
export const textStyles = {
  metricXL: { family: 'heading', weight: 'bold', size: 64, lineHeight: 68, letterSpacing: -2 },
  metricL: { family: 'heading', weight: 'bold', size: 44, lineHeight: 48, letterSpacing: -1 }, // web auth brand panel headline
  display: { family: 'heading', weight: 'bold', size: 34, lineHeight: 40, letterSpacing: -0.5 },
  headingPage: { family: 'heading', weight: 'bold', size: 32, lineHeight: 38, letterSpacing: -0.4 },
  headingH1: { family: 'heading', weight: 'semibold', size: 28, lineHeight: 34, letterSpacing: -0.4 },
  headingH2: { family: 'heading', weight: 'semibold', size: 22, lineHeight: 28, letterSpacing: -0.3 },
  headingH3: { family: 'heading', weight: 'medium', size: 18, lineHeight: 24, letterSpacing: -0.2 },
  headingH4: { family: 'heading', weight: 'medium', size: 16, lineHeight: 22, letterSpacing: 0 },
  titleSheet: { family: 'heading', weight: 'bold', size: 22, lineHeight: 28, letterSpacing: -0.4 },
  titleDrawer: { family: 'heading', weight: 'regular', size: 18, lineHeight: 24, letterSpacing: 0 },
  titleBar: { family: 'heading', weight: 'regular', size: 17, lineHeight: 22, letterSpacing: 0 },

  bodyLarge: { family: 'sans', weight: 'regular', size: 17, lineHeight: 22, letterSpacing: -0.4 },
  bodyMedium: { family: 'sans', weight: 'regular', size: 15, lineHeight: 20, letterSpacing: -0.2 },
  bodyCompact: { family: 'sans', weight: 'regular', size: 14, lineHeight: 20, letterSpacing: 0 },
  bodySmall: { family: 'sans', weight: 'regular', size: 13, lineHeight: 18, letterSpacing: 0 },
  bodyXSmall: { family: 'sans', weight: 'regular', size: 12, lineHeight: 16, letterSpacing: 0 },

  labelLarge: { family: 'sans', weight: 'semibold', size: 15, lineHeight: 20, letterSpacing: 0.1 },
  labelMedium: { family: 'sans', weight: 'semibold', size: 13, lineHeight: 18, letterSpacing: 0 },
  labelSmall: { family: 'sans', weight: 'semibold', size: 11, lineHeight: 14, letterSpacing: 0.1 },
  labelTag: { family: 'sans', weight: 'bold', size: 10.5, lineHeight: 14, letterSpacing: 0.6 },

  caption: { family: 'sans', weight: 'regular', size: 11, lineHeight: 14, letterSpacing: 0 },
  /** Staff rail group header (Figma "Admin/Sidebar"): the 24px row above each nav group when the rail is open. */
  railGroup: {
    family: 'sans',
    weight: 'semibold',
    size: 10,
    lineHeight: 12,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  overline: {
    family: 'heading',
    weight: 'semibold',
    size: 11,
    lineHeight: 14,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  code: { family: 'mono', weight: 'regular', size: 13, lineHeight: 18, letterSpacing: 0 },
} as const satisfies Record<string, TextStyleToken>;

/** Kept for backwards compatibility with v1 consumers (now px numbers). */
export const typography = {
  fontFamily,
  fontWeight,
  fontSize: { xs: 12, sm: 13, md: 14, base: 15, lg: 17, xl: 18, '2xl': 22, '3xl': 28, '4xl': 34, '5xl': 64 },
  textStyles,
} as const;

/* ------------------------------------------------------------------ */
/* 5. Spacing, layout, radii, sizes                                   */
/* ------------------------------------------------------------------ */

/** 4-pt scale (Figma Dimension/space/1–9). */
export const spacing = {
  0: 0,
  0.5: 2,
  0.75: 3, // the inset of a compact segmented track (Figma A04.1)
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  3.5: 14,
  4: 16,
  4.5: 18, // Figma C03.1 "Completed" row
  5: 20,
  5.5: 22, // Figma C03.1 / C03.2 side cards
  6: 24,
  7: 32,
  8: 40,
  9: 56,
} as const;

export const layout = {
  screenWidth: 393, // design frame (iPhone 15/16)
  screenPadding: 20,
  sectionGap: 28,
  cardPadding: 16,
  listRowPadding: 14,
  headerHeight: 56,
  tabBarHeight: 68,
  tabBarInset: 16, // floating tab bar side margin
  sheetHandle: { width: 40, height: 5 },
  sheetTopPadding: 10,
  maxContentWidth: 560, // web / tablet
  // Web overlays. KT §5.8 gives drawers 480–560px; 520 / 440 are provisional midpoints until the A04.1
  // "Add staff" drawer and confirm-dialog frames are read - correct them here, not in components.
  // Web auth screens (Figma "C01.x" 1440x900): brand panel 640 wide, form column 400 wide.
  authPanelWidth: 640,
  authFormWidth: 400,
  scrollbarSize: 3, // web scrollbars: as thin and quiet as possible
  // Staff app shell (Figma A01.3): 72 icon rail that hovers open to 240, 60 top bar, content up to 1136 wide.
  railWidth: 72,
  railExpandedWidth: 240,
  topbarHeight: 60,
  topbarSearchWidth: 380,
  toolbarSearchNarrowWidth: 200, // Figma A05.1 / A05.2: the compact filled search next to a segmented control
  suspendSelectWidth: 230, // Figma A04.1 "Suspend access": the select on each open-work line
  rowMenuWidth: 248, // Figma A04.1 "Row menu"
  taskMenuWidth: 272, // Figma C03.3 the client task page's "⋯" menu
  pickerPopoverWidth: 380, // Figma A05.4d "Add people" popover
  pickerListMaxHeight: 400, // the scrolling people list inside it
  formDrawerWidth: 480, // Figma A05.1b "New project": a create-form sheet, narrower than the settings drawers
  formDrawerPadding: 28,
  toolbarSearchWidth: 300, // Figma A03.1 / A04: the table toolbar's search pill
  vaultSearchWidth: 280, // Figma C03.4: the account deliverables search pill
  vaultDrawerWidth: 560, // Figma A05.11b "Add to vault": a wider create-form sheet
  vaultActionWidth: 96, // Figma A05.11: the Reveal / Download / Copy link pill at the end of a row
  vaultFieldLabelWidth: 150, // ...the name box of one of its fields (the value takes the rest)
  taskActionWidth: 240, // Figma C03.10: the dark "Review in Workbench" / "View your brief" button under the stage
  workingTextWidth: 340, // Figma C03.10a: "Aisha is working on it" and the sentence under it
  workingBarWidth: 240, // ...and its progress bar
  toastWidth: 400, // Figma P01 toasts: fixed 400, bottom-right
  boardLaneMinWidth: 245, // Figma A05.7: the narrowest a lane of the My work board gets before the board scrolls sideways
  peekBarWidth: 150, // Figma A05.7d: the progress bar in the quick peek's footer
  stateTextWidth: 420, // Figma P01 empty / error / 403 / 404: the sentence under the title wraps at 420
  toolbarSearchMidWidth: 260, // Figma A05.5 review queue: the filled search beside the queue tabs
  numberFieldWidth: 104, // a short numeric field beside a name field (milestone stop %, effort units)
  staffContentWidth: 1136,
  // Staff settings (Figma A16, 1440 frames): 1200 canvas = 240 sub-nav + 32 gap + 928 content; the floating
  // "unsaved changes" bar is 640 wide.
  staffSettingsWidth: 1200,
  staffWideMaxWidth: 1800, // the widest a workspace page grows on a big window (it is 1200 at the 1440 design)
  staffBoardGutter: 44, // Figma A05.7 "My work": the board is 1280 across at 1440 (44 either side), five lanes need the room
  staffGutter: 84, // Figma: the space either side of the 1200 content at 1440. Wider windows give the content the extra.
  staffSettingsNavWidth: 240,
  unsavedBarWidth: 640,
  qrSize: 112, // authenticator QR code in the "Turn on two-step" dialog
  menuWidth: 296, // profile menu popover (Figma 429:29276)
  notificationsWidth: 384, // notifications popover (Figma 429:29056)
  dialogWideWidth: 560, // "Add staff" (A04.1) modal
  dialogListMaxHeight: 320, // scrolling pick-list inside a dialog ("Assign account manager", A03.1)
  // Client app shell (Figma Section 02, 1440 frames): 248 sidebar; Settings = 200 sub-nav + 848 content.
  sidebarWidth: 248,
  settingsNavWidth: 200,
  settingsContentWidth: 848,
  scanCardWidth: 500, // "C01.10 · Audit scanning" card
  authProofWidth: 330, // stat tiles + testimonial column inside the brand panel
  drawerWidth: 520,
  dialogWidth: 440,
} as const;

/** Figma Dimension/radius + component-level radii. */
export const radii = {
  none: 0,
  xs: 6,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  full: 999,
} as const;

export const componentRadii = {
  button: radii.full,
  chip: radii.full,
  input: radii.md,
  iconTile: 12,
  checkbox: 7, // Figma "Checkbox" (116:772) Box
  railItem: 10, // Figma "Rail/*" buttons
  optionTile: 14, // Figma A04.1 "Suspend access": a choice card and the grey box under it
  identityTile: 18, // Figma "Business header": the 64px initials tile
  tableAvatar: 10, // Figma A03.1 / A04 table rows: square-ish business and person tile
  segmentOption: 10, // Figma A04.1 compact segmented option
  card: 20,
  widget: 24,
  sheet: radii.xl, // top corners only
  popover: radii.lg,
  bubble: 18,
  revealedLogin: 18, // Figma C03.4: the card of a login that is open
  slide: 14, // Figma C03.10: one slide of a carousel in the client's task stage
  vaultField: 10, // Figma A05.11b: the name and value boxes inside a field block
  stateTile: 14, // Figma P01: the 48px icon tile above an empty / error / 403 / 404 title
  toast: 14, // Figma P01 toast
  boardColumn: 18, // Figma A05.7: a lane of the My work board
  boardCard: 14, // ...and a card in it
  boardNote: 10, // ...the amber note on a card that was sent back
  gauge: 14, // ...the capacity gauge
  peekNote: 12, // Figma A05.7d: the latest-note block of the quick peek
  peekThumb: 10, // ...a version thumbnail
  stateCard: 20, // Figma P01 dialogs (destructive confirm, session expired)
  /** Message Bubble Type=Reply/Task/Image - richer preview cards use a bigger radius than plain text/file/voice bubbles. */
  bubbleRich: 20,
  thumb: 12,
} as const;

export const sizes = {
  hitTarget: 44,
  iconButton: 44,
  iconTile: 34, // Figma "List Row" (116:923) leading icon tile
  railItem: { width: 44, height: 34 }, // staff rail button (Figma "Rail/*", updated: 34 tall)
  iconTileLarge: 36, // C01 welcome value rows / place results
  chipMd: 26, // Figma A04 skill chip (outlined pill)
  toolbarControlLg: 38, // Figma A05.1 / A05.2 toolbars and the header actions of a business, project or person
  toolbarControl: 34, // Figma A03.1 / A04 toolbar: search, filter pills and export are all 34 tall
  carouselSlide: 132, // Figma C03.10: a carousel slide in the task stage (4:5, so 165 tall)
  stateTile: 48, // Figma P01: the icon tile of an empty / error / 403 / 404 state
  gaugeSegment: 16, // Figma A05.7: one segment of the capacity gauge (6 tall, 3 apart)
  peekThumb: 180, // Figma A05.7d: a version thumbnail in the quick peek (three across)
  queueIconBox: 36, // Figma A05.7c: the type tile at the start of a queue row
  peekFooter: 78, // Figma A05.7d: the sticky footer of the quick peek
  iconTileProject: 40, // Figma C03.1 / A05.1 project card + row domain tile
  sliderThumb: 24, // Figma A05.4 progress track: the handle (a 3px dark ring around white)
  milestoneMarker: 20, // Figma A05.4 progress track: a milestone stop (filled + check when done)
  mascot: { width: 200, height: 220 }, // Figma "Mascot" instance
  avatar: { xxs: 24, xs: 28, sm: 32, md: 40, lg: 48, xl: 64 },
  button: { hero: 54, primary: 56, compact: 50, small: 36, row: 40, review: 52, dialog: 44 }, // row: Figma A16 settings-row buttons; review: Figma C05.2 "Your review" actions; dialog: Figma P01 confirm / session dialogs
  input: 56,
  inputFilled: 44, // Figma A16 settings forms: grey-filled field, 44 tall
  inputCompact: 42, // Figma A04.1 "Add staff" dialog fields: white, 1px stroke, radius 12
  searchField: 48,
  listRow: { compact: 56, default: 64, tall: 76 },
  segmented: 40,
  vaultInput: 40, // Figma A05.11b: a name or value box inside a field block
  segmentedPill: 28, // Figma C03.10a: the lone "Brief" pill in the task card's header
  tableHeadSm: 34, // Figma A03.2 "Open work": the grey head of a table inside a card
  tableRowSm: 52, // ...and one of its rows
  reviewRow: 66, // Figma A05.5 review queue: one draft
  reviewHead: 38, // ...and the head above the rows
  railDot: 7, // Figma A01.3 "Dot": the blue mark on a rail item that has something waiting
  taskStage: 470, // Figma C03.3 the stage of the client's task page: its height
  reviewStage: 720, // Figma C05.2 the sky stage under the client's review: its height
  igPreview: 360, // ...the Instagram preview card's width
  reelPreview: 276, // ...a reel's portrait frame (9:16)
  versionThumb: 56, // ...the chosen version in the strip below the stage
  versionThumbSmall: 42, // ...and the others
  assetThumb: 72, // Figma A05.6 "Brief assets": one thumbnail
  sectionThumb: 48, // Figma A05.x website submit "Homepage sections": a section's screenshot
  jumpRow: 42, // Figma A03.2 "Everything for this client": one link
  banner: 38, // Figma A03.2 "Override mode banner": the full-width strip under the top bar
  menuItem: 38, // Figma A04.1 "Row menu": one entry
  segmentedCompact: 40, // Figma A04.1 "Add staff" role picker: a 40px track holding 34px options
  // Figma "Toggle" component set (116:753) is 208x60 holding 3 variants at 16px padding/gap -> 48x28 each.
  toggle: { width: 48, height: 28, knob: 22 },
  checkbox: 22,
  radio: 22,
  tagHeight: 24,
  otpCell: { width: 50, height: 60 }, // mobile
  otpCellWide: { width: 58, height: 60 }, // web (Figma "C01.9 · Verify email"): 6 cells + 10px gaps = the 400px form column
} as const;

export const iconSizes = {
  xs: 12,
  sm: 16,
  md: 18, // default in buttons/rows
  lg: 20,
  xl: 24, // tab bar, header buttons
  state: 22, // Figma P01: the glyph in the 48px tile of an empty / error / 403 / 404 state
  strokeWidth: 1.5, // Phosphor-style outline icons
} as const;

/* ------------------------------------------------------------------ */
/* 6. Elevation (Figma effect styles Elevation/sm, md, lg, glow-blue) */
/* ------------------------------------------------------------------ */

type ShadowToken = {
  color: string;
  opacity: number;
  offsetX: number;
  offsetY: number;
  blur: number;
  spread: number;
  /** Android elevation approximation */
  elevation: number;
};

export const shadows = {
  none: { color: palette.ink, opacity: 0, offsetX: 0, offsetY: 0, blur: 0, spread: 0, elevation: 0 },
  sm: { color: palette.grey900, opacity: 0.06, offsetX: 0, offsetY: 1, blur: 3, spread: 0, elevation: 1 },
  md: { color: palette.ink, opacity: 0.08, offsetX: 0, offsetY: 8, blur: 20, spread: -6, elevation: 4 },
  lg: { color: palette.grey900, opacity: 0.18, offsetX: 0, offsetY: 20, blur: 40, spread: -8, elevation: 12 },
  tableCard: { color: palette.grey900, opacity: 0.08, offsetX: 0, offsetY: 8, blur: 24, spread: 0, elevation: 4 }, // Figma A03.1 "Card · Table" (its second layer is `sm`)
  glowBlue: { color: palette.blue600, opacity: 0.35, offsetX: 0, offsetY: 10, blur: 25, spread: -5, elevation: 8 },
  glowGreen: { color: palette.green600, opacity: 0.45, offsetX: 0, offsetY: 10, blur: 25, spread: -5, elevation: 8 },
  glowPro: { color: '#312e81', opacity: 0.5, offsetX: 0, offsetY: 10, blur: 25, spread: -5, elevation: 8 },
  focusRing: { color: palette.blue600, opacity: 0.35, offsetX: 0, offsetY: 0, blur: 0, spread: 3, elevation: 0 },
} as const satisfies Record<string, ShadowToken>;

export const blur = {
  glass: 20, // header buttons, quick actions on sky
  sheetBackdrop: 30,
  sky: 60, // cloud shapes
} as const;

/* ------------------------------------------------------------------ */
/* 7. Motion & layering                                               */
/* ------------------------------------------------------------------ */

export const motion = {
  duration: { instant: 100, fast: 150, base: 220, slow: 320, sheet: 380 },
  easing: {
    standard: [0.2, 0, 0, 1],
    enter: [0, 0, 0.2, 1],
    exit: [0.4, 0, 1, 1],
    emphasized: [0.3, 0, 0, 1],
  },
  spring: {
    sheet: { damping: 28, stiffness: 300, mass: 1 },
    press: { damping: 20, stiffness: 400, mass: 0.6 },
  },
  pressScale: 0.97,
} as const;

export const zIndex = {
  base: 0,
  raised: 10,
  header: 20,
  tabBar: 30,
  overlay: 40,
  sheet: 50,
  popover: 60,
  toast: 70,
} as const;

export const opacity = {
  disabled: 0.4,
  pressed: 0.85,
  muted: 0.6,
} as const;

/* ------------------------------------------------------------------ */
/* 8. Domain badges                                                   */
/* ------------------------------------------------------------------ */

type Badge = { bg: string; text: string; border: string; label: string };
const tag = (t: keyof typeof colors.tag, border: string, label: string): Badge => ({
  bg: colors.tag[t].bg,
  text: colors.tag[t].text,
  border,
  label,
});

/** Operational roles (Admin → Account Manager → Lead Specialist → Specialist → Client). */
export const roles = {
  admin: tag('amber', '#fcd9a8', 'Agency Admin'),
  account_manager: tag('pink', '#f0c6fa', 'Account Manager'),
  lead_specialist: tag('indigo', '#c7cdfb', 'Lead Specialist'),
  specialist: tag('green', '#bfe8cf', 'Specialist'),
  client: tag('blue', '#c9d8fd', 'Client'),
} as const satisfies Record<string, Badge>;

/**
 * Two-tier review statuses. `label` is the internal/agency label,
 * `clientLabel` is what the client sees in the app (null = hidden from client).
 */
export const status = {
  DRAFT: { ...tag('grey', palette.grey300, 'Draft'), clientLabel: null },
  PENDING_LEAD_REVIEW: { ...tag('amber', '#fcd9a8', 'Pending Lead Review'), clientLabel: null },
  APPROVED_BY_LEAD: { ...tag('green', '#bfe8cf', 'Approved by Lead'), clientLabel: null },
  PUBLISHED_TO_CLIENT: { ...tag('blue', '#c9d8fd', 'Published to Client'), clientLabel: 'Ready for your review' },
  REVISION_REQUESTED: { ...tag('red', '#f8c9c9', 'Revision Requested'), clientLabel: 'Changes asked' },
  COMPLETED: { ...tag('green', '#bfe8cf', 'Completed'), clientLabel: 'Approved' },
} as const;

/** Client-facing task lifecycle (Projects / Work tabs). */
export const taskStatus = {
  QUEUED: tag('grey', palette.grey300, 'Queued'),
  IN_PROGRESS: tag('indigo', '#c7cdfb', 'In progress'),
  IN_REVIEW: tag('blue', '#c9d8fd', 'Ready for your review'),
  CHANGES_ASKED: tag('red', '#f8c9c9', 'Changes asked'),
  APPROVED: tag('green', '#bfe8cf', 'Approved'),
  SCHEDULED: tag('amber', '#fcd9a8', 'Scheduled'),
  LIVE: tag('green', '#bfe8cf', 'Live'),
} as const satisfies Record<string, Badge>;

export const bookingStatus = {
  REQUESTED: { bg: colors.tag.amber.bg, text: colors.tag.amber.text, label: 'Requested' },
  CONFIRMED: { bg: colors.tag.blue.bg, text: colors.tag.blue.text, label: 'Confirmed' },
  COMPLETED: { bg: colors.tag.green.bg, text: colors.tag.green.text, label: 'Completed' },
  CANCELLED: { bg: colors.tag.red.bg, text: colors.tag.red.text, label: 'Cancelled' },
} as const;

/** Meeting types driving the Home CTA (see KT doc §7). */
export const meetingTypes = {
  // Figma "Home CTA" component, Property 1=Meeting CTA: gradient corrected to match the
  // actual instance fills (#2563eb → #4f46e5 → #1d4ed8, i.e. `ctaPrimary`) — these three
  // previously pointed at `hero`, the much paler Welcome-screen button gradient.
  FREE_CHAI: { label: 'Free chai 1-on-1', cta: 'Book your free chai', minutes: 15, gradient: 'ctaPrimary' },
  DISCOVERY: { label: 'Discovery call', cta: 'Book your discovery call', minutes: 30, gradient: 'ctaPrimary' },
  MONTHLY_REVIEW: {
    label: 'Monthly review',
    cta: 'Book your monthly review',
    minutes: 30,
    gradient: 'ctaPrimary',
  },
  BUSINESS_CONSULTATION: {
    label: 'Business consultation',
    cta: 'Book a business consultation',
    minutes: 60,
    gradient: 'pro',
  },
} as const;

/** Home CTA · Property 2=Booked (post-booking confirmation state, not a bookable type itself). */
export const meetingBookedCta = {
  cta: 'Join chai · {day}, {time}', // {day}/{time} are caller-filled placeholders
  gradient: 'success',
} as const;

/** Home CTA · Property 2=Fallback (no upcoming meeting / nothing booked yet). */
export const meetingFallbackCta = {
  cta: "This month's report",
  gradient: 'ctaPrimary',
  secondaryLabel: 'Manager',
} as const;

/** Plans — marketing name in UI, internal tier in code. Payments happen on web only. */
export const plans = {
  free: { name: 'First Chai', tier: 'free', badge: colors.tag.grey },
  basic: { name: 'Mate', tier: 'basic', badge: colors.tag.blue },
  // Figma "Account menu" plan pill (137:2375): bg/text are blue50/blue700, not
  // colors.tag.indigo (eef0ff/#4338ca) — close enough to have been a plausible guess,
  // but not what's actually in the file.
  plus: { name: 'Right Hand Mate', tier: 'plus', badge: { bg: palette.blue50, text: palette.blue700 } },
  pro: { name: 'Business Partner', tier: 'pro', badge: { bg: palette.ink, text: palette.amber400 } },
} as const;

/**
 * Report Plan Card (Figma "Flow 09 · Report", "9.3 Recommended plan · 3 variants", node
 * 409:24132) - the richer per-tier visual treatment the upsell card itself needs, beyond the
 * small `plans` badge above: accent color, "why we picked it" chip/box background, card
 * surface/border, and text colors (tier=pro is a dark card, so its text colors invert).
 */
export const planCardColors = {
  basic: {
    accent: palette.blue,
    soft: palette.blue50,
    surface: colors.surface.card,
    border: colors.border.default,
    heading: colors.text.primary,
    body: colors.text.body,
    cta: 'outline',
  },
  plus: {
    accent: palette.violet,
    soft: palette.planVioletSoft,
    surface: colors.surface.card,
    border: palette.planLavender,
    heading: colors.text.primary,
    body: colors.text.body,
    cta: 'gradient',
    gradient: 'planPlus',
  },
  pro: {
    accent: palette.planGold,
    soft: palette.planInkRaised,
    surface: palette.planInk,
    border: palette.planInkBorder,
    heading: colors.text.onDark,
    body: palette.planInkText,
    cta: 'gradient',
    gradient: 'planPro',
  },
} as const;

/* ------------------------------------------------------------------ */
/* 9. Helpers                                                         */
/* ------------------------------------------------------------------ */

/** Web: 16 → "16px" */
export const px = (n: number): string => `${n}px`;
/** Web: 16 → "1rem" (16px root) */
export const rem = (n: number, root = 16): string => `${n / root}rem`;

/** Hex + alpha → rgba string. withAlpha('#2563eb', 0.35) */
export const withAlpha = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h.slice(0, 6);
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** Shadow token → React Native style props. */
export const shadowToRN = (s: ShadowToken) => ({
  shadowColor: s.color,
  shadowOpacity: s.opacity,
  shadowOffset: { width: s.offsetX, height: s.offsetY },
  shadowRadius: s.blur / 2,
  elevation: s.elevation,
});

/** Shadow token → CSS box-shadow. */
export const shadowToCSS = (s: ShadowToken): string =>
  `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${withAlpha(s.color, s.opacity)}`;

/** Gradient token → props for react-native-linear-gradient / expo-linear-gradient. */
export const gradientToRN = (g: { angle: number; stops: readonly string[]; locations?: readonly number[] }) => {
  const rad = ((g.angle - 90) * Math.PI) / 180;
  const x = Math.cos(rad) / 2;
  const y = Math.sin(rad) / 2;
  return {
    colors: [...g.stops],
    start: { x: 0.5 - x, y: 0.5 - y },
    end: { x: 0.5 + x, y: 0.5 + y },
    ...(g.locations ? { locations: [...g.locations] } : {}),
  };
};

/** Gradient token → CSS linear-gradient(). */
export const gradientToCSS = (g: {
  angle: number;
  stops: readonly string[];
  locations?: readonly number[];
}): string => {
  const stops = g.locations ? g.stops.map((c, i) => `${c} ${g.locations![i] * 100}%`) : g.stops;
  return `linear-gradient(${g.angle}deg, ${stops.join(', ')})`;
};

/** Text style token → React Native TextStyle (uses nativeFonts). */
export const textStyleToRN = (t: TextStyleToken) => {
  const fam = t.family === 'heading' ? nativeFonts.outfit : t.family === 'mono' ? nativeFonts.mono : nativeFonts.geist;
  const fontFamilyName = (fam as Record<string, string>)[t.weight] ?? (fam as Record<string, string>).regular;
  return {
    fontFamily: fontFamilyName,
    fontSize: t.size,
    lineHeight: t.lineHeight,
    letterSpacing: t.letterSpacing,
    ...(t.textTransform ? { textTransform: t.textTransform } : {}),
  };
};

/* ------------------------------------------------------------------ */
/* 10. Theme object + types                                           */
/* ------------------------------------------------------------------ */

export const theme = {
  palette,
  colors,
  gradients,
  typography,
  textStyles,
  spacing,
  layout,
  radii,
  componentRadii,
  sizes,
  iconSizes,
  shadows,
  blur,
  motion,
  zIndex,
  opacity,
} as const;

export type Theme = typeof theme;
export type Colors = typeof colors;
export type Typography = typeof typography;
export type TextStyleKey = keyof typeof textStyles;
export type Radii = typeof radii;
export type Spacing = typeof spacing;
export type ShadowKey = keyof typeof shadows;
export type GradientKey = keyof typeof gradients;
export type TagTone = keyof typeof colors.tag;
export type RoleBadgeKey = keyof typeof roles;
export type StatusBadgeKey = keyof typeof status;
export type TaskStatusKey = keyof typeof taskStatus;
export type BookingStatusBadgeKey = keyof typeof bookingStatus;
export type MeetingTypeKey = keyof typeof meetingTypes;
export type PlanKey = keyof typeof plans;
export type PlanTier = keyof typeof planCardColors;
